import { logger } from '@springtree/eva-sdk-core-logger';
import { Core } from '@springtree/eva-services-core';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';
import getEvaEndpoint from '../helpers/get-eva-endpoint.js';
import currentUserTokenState from '../state/current-user/current-user-token-state.js';
import evaEndpointUrlState from '../state/core/eva-endpoint-url-state.js';
import requestedOrganizationUnitIdState from '../state/core/requested-organization-unit-id-state.js';
import requestedOrganizationUnitQueryState from '../state/core/requested-organization-unit-query-state.js';
import shoppingCartState from '../state/checkout/shopping-cart-state.js';
import useGetState from './use-get-state.js';
import storage from '../helpers/storage.js';
export const useShoppingCartAdd = ({ service, options = {}, }) => {
    const evaEndpointUrl = useGetState(evaEndpointUrlState);
    const currentUserToken = useGetState(currentUserTokenState);
    const evaRequestedOrganizationUnitID = useGetState(requestedOrganizationUnitIdState);
    const evaRequestedOrganizationUnitQuery = useGetState(requestedOrganizationUnitQueryState);
    const shoppingCart = useGetState(shoppingCartState.response);
    const shoppingCartRequest = useGetState(shoppingCartState.request);
    const setCurrentUserToken = useSetRecoilState(currentUserTokenState);
    const setShoppingCartRequest = useSetRecoilState(shoppingCartState.request);
    const setShoppingCartStale = useSetRecoilState(shoppingCartState.stale);
    const addToCart = useCallback(async ({ callOptions, createNewOrderWhen, orderType = 0, // EVA.Core.OrderTypes.Sales,
    payload, hookCallOptions, }) => {
        try {
            if (!evaEndpointUrl) {
                return;
            }
            const evaEndpoint = await getEvaEndpoint(evaEndpointUrl);
            // First check if we have an order and if it is not in a completed status
            // Create a new order if there is none or if it is completed
            //
            const requestedOrderId = shoppingCartRequest?.OrderID;
            const currentOrderId = shoppingCart?.ShoppingCart.ID;
            const isCompleted = shoppingCart?.ShoppingCart.IsCompleted === true;
            const isPaid = shoppingCart?.ShoppingCart.IsPaid === true;
            const createNewOrder = (createNewOrderWhen === 'completed' && isCompleted) || (createNewOrderWhen === 'paid' && isPaid);
            let newOrderId;
            if (!currentOrderId || createNewOrder) {
                const newOrder = await evaEndpoint.callService(Core.CreateOrder, {
                    Type: orderType,
                }, {
                    authenticationToken: currentUserToken,
                    requestedOrganizationUnitID: evaRequestedOrganizationUnitID,
                    requestedOrganizationUnitQuery: evaRequestedOrganizationUnitQuery,
                    ...callOptions,
                });
                newOrderId = newOrder.OrderID;
            }
            else {
                newOrderId = currentOrderId;
            }
            const requestPayload = {
                ...payload,
            };
            // Get the target service name. We must use an instance because the
            // name property on the prototype will change by the code bundler
            //
            const serviceName = new service().name;
            // If this is a create customer request we have to check a few things:
            //
            // * there is no current customer assigned to the cart
            // * auto login is enabled on the register call
            //
            if (serviceName === 'Core:CreateCustomer') {
                if (shoppingCart?.ShoppingCart?.Customer?.ID) {
                    throw new Error('The current order already has an attached customer');
                }
                if (!payload.AutoLogin) {
                    throw new Error('Auto-login must be enabled to attach the customer to the order');
                }
            }
            // Add the target order id property in the request
            //
            requestPayload.OrderID = newOrderId;
            // Options are combined in such a way that the caller can override
            // the token if desired
            //
            const response = await evaEndpoint.callService(service, requestPayload, {
                authenticationToken: currentUserToken,
                requestedOrganizationUnitID: evaRequestedOrganizationUnitID,
                requestedOrganizationUnitQuery: evaRequestedOrganizationUnitQuery,
                ...callOptions,
            });
            // The wrap-up for create customer and login is slightly different
            // The new user token must be set on the react context and the current
            // stored anonymous cart state needs to be migrated.
            // We will unset and reset the storage key values if present so that when
            // the context re-initializes it will retrieve the correct initial atom
            // values from storage
            //
            if (serviceName === 'Core:CreateCustomer') {
                const storageKey = `${evaEndpointUrl.replace(/https?:\/\//, '')}:default:shoppingCartDetails`;
                const storedCurrentOrderValue = storage.getItem(storageKey);
                const newUserId = response.User?.ID;
                const newUserToken = response.User?.AuthenticationToken || response.User.AuthenticationToken;
                if (storedCurrentOrderValue && newUserId && newUserToken) {
                    const newStorageKey = `${evaEndpointUrl.replace(/https?:\/\//, '')}:${newUserId}:shoppingCartDetails`;
                    storage.setItem(newStorageKey, storedCurrentOrderValue);
                    setCurrentUserToken(newUserToken);
                }
            }
            if (requestedOrderId !== newOrderId) {
                // Set the new current shopping cart
                //
                setShoppingCartRequest({
                    ...shoppingCartRequest,
                    OrderID: newOrderId,
                });
            }
            else if (hookCallOptions?.doNotSetStale !== true) {
                // Mark the existing shopping cart as being stale
                // This can be disabled for performing multiple cart modifying actions in bulk
                //
                setShoppingCartStale(`${new Date().valueOf()}`);
            }
            if (response && options.onSuccess) {
                options.onSuccess(response);
            }
            return response;
        }
        catch (error) {
            logger.error(`Failed to call service: ${service.name}`, error)();
            if (options.onError) {
                options.onError(error);
            }
        }
    }, [
        currentUserToken,
        evaEndpointUrl,
        evaRequestedOrganizationUnitID,
        evaRequestedOrganizationUnitQuery,
        service,
        shoppingCart,
        setShoppingCartRequest,
    ]);
    return addToCart;
};
export default useShoppingCartAdd;
//# sourceMappingURL=use-shopping-cart-add.js.map